import useTranslation from '../useTranslation';
import { useState } from 'react';

import { useFormContext, useFormWatch } from '@american-autonomy/ag-ui';

import {
    BOUNDARIES_FIELD_NAME,
    FIELD_UPDATE_OPTIONS,
    PRIMARY_BOUNDARY_FIELD_NAME,
    REASSIGN_ASSOCIATED_ACTION,
    REASSIGN_PRIMARY_ACTION,
    SUCCESS_VARIANT,
    UNASSIGN_PRIMARY_ACTION
} from './constants';
import type {
    BoundaryFieldSchema,
    BoundaryModalAction,
    FieldFormSchema,
    ModalConfig,
    Props,
    UseBoundarySelectionsReturn
} from './types';

const useBoundarySelections = ({ siteId }: Props): UseBoundarySelectionsReturn => {
    const { control, setValue } = useFormContext<FieldFormSchema>();
    const {
        MOVE,
        REASSIGN_BOUNDARY_SUB_TEXT,
        REASSIGN_BOUNDARY_TEXT,
        REMOVE,
        UNASSIGN_LAST_BOUNDARY_SUB_TEXT,
        UNASSIGN_PRIMARY_SUB_TEXT,
        UNASSIGN_PRIMARY_TEXT,
        UNKNOWN_FIELD
    } = useTranslation();

    const [pendingAction, setPendingAction] = useState<BoundaryModalAction | null>(null);

    const boundaries = useFormWatch({ control, name: BOUNDARIES_FIELD_NAME }) ?? [];
    const primaryBoundary = useFormWatch({ control, name: PRIMARY_BOUNDARY_FIELD_NAME }) ?? null;

    const isOwnedByOtherField = ({ siteId: boundarySiteId }: BoundaryFieldSchema) =>
        !!boundarySiteId && boundarySiteId !== siteId;

    const isSelected = ({ value }: BoundaryFieldSchema) =>
        boundaries.some(boundary => boundary.value === value);

    const withoutBoundary = ({ value }: BoundaryFieldSchema) =>
        boundaries.filter(boundary => boundary.value !== value);

    // Promoting a boundary to primary takes it out of the associated selections, so a boundary is
    // never represented in both places. The boundary it replaces is demoted into the associated
    // list: replacing the primary changes which boundary leads the field, it does not remove the
    // previous one from the field. Only the explicit unassign action does that.
    const commitPrimary = (boundary: BoundaryFieldSchema) => {
        const isReplacingDifferentBoundary =
            !!primaryBoundary && primaryBoundary.value !== boundary.value;

        const nextBoundaries = isReplacingDifferentBoundary
            ? [...withoutBoundary(boundary), primaryBoundary]
            : withoutBoundary(boundary);

        setValue(PRIMARY_BOUNDARY_FIELD_NAME, boundary, FIELD_UPDATE_OPTIONS);
        setValue(BOUNDARIES_FIELD_NAME, nextBoundaries, FIELD_UPDATE_OPTIONS);
    };

    const commitBoundaryToggle = (boundary: BoundaryFieldSchema) => {
        const nextBoundaries = isSelected(boundary)
            ? withoutBoundary(boundary)
            : [...boundaries, boundary];

        setValue(BOUNDARIES_FIELD_NAME, nextBoundaries, FIELD_UPDATE_OPTIONS);
    };

    const handlePrimarySelected = (boundary: BoundaryFieldSchema) => {
        if (isOwnedByOtherField(boundary)) {
            setPendingAction({ boundary, type: REASSIGN_PRIMARY_ACTION });

            return;
        }

        commitPrimary(boundary);
    };

    const handleBoundaryToggle = (boundary: BoundaryFieldSchema) => {
        if (!isSelected(boundary) && isOwnedByOtherField(boundary)) {
            setPendingAction({ boundary, type: REASSIGN_ASSOCIATED_ACTION });

            return;
        }

        commitBoundaryToggle(boundary);
    };

    const handleBoundaryRemove = (boundary: BoundaryFieldSchema) => {
        setValue(BOUNDARIES_FIELD_NAME, withoutBoundary(boundary), FIELD_UPDATE_OPTIONS);
    };

    // A field with boundaries always has a primary, so removing the primary warns that the
    // backend will promote the earliest remaining boundary in its place.
    const handlePrimaryRemove = () => {
        if (!primaryBoundary) {
            return;
        }

        setPendingAction({
            boundary: primaryBoundary,
            hasRemaining: boundaries.length > 0,
            type: UNASSIGN_PRIMARY_ACTION
        });
    };

    const handleModalClose = () => {
        setPendingAction(null);
    };

    // Confirming records the intent in the form and writes nothing. Every boundary change,
    // including moving one off another field, is sent when the form is submitted, so cancelling
    // or discarding the form leaves the server untouched.
    const handleModalConfirm = () => {
        if (!pendingAction) {
            return;
        }

        const { boundary, type } = pendingAction;

        if (type === UNASSIGN_PRIMARY_ACTION) {
            setValue(PRIMARY_BOUNDARY_FIELD_NAME, null, FIELD_UPDATE_OPTIONS);
            setPendingAction(null);

            return;
        }

        // Staged against this field so the panel groups it with the field's own boundaries and
        // does not prompt again while the selection stands.
        const movedBoundary: BoundaryFieldSchema = { ...boundary, siteId };

        if (type === REASSIGN_PRIMARY_ACTION) {
            commitPrimary(movedBoundary);
        } else {
            commitBoundaryToggle(movedBoundary);
        }

        setPendingAction(null);
    };

    const getModalConfig = (action: BoundaryModalAction): ModalConfig => {
        if (action.type === UNASSIGN_PRIMARY_ACTION) {
            return {
                confirmText: REMOVE,
                subText: action.hasRemaining
                    ? UNASSIGN_PRIMARY_SUB_TEXT
                    : UNASSIGN_LAST_BOUNDARY_SUB_TEXT,
                text: UNASSIGN_PRIMARY_TEXT({ boundaryName: action.boundary.label })
            };
        }

        return {
            confirmText: MOVE,
            confirmVariant: SUCCESS_VARIANT,
            subText: REASSIGN_BOUNDARY_SUB_TEXT({
                boundaryName: action.boundary.label,
                fieldName: action.boundary.siteName ?? UNKNOWN_FIELD
            }),
            text: REASSIGN_BOUNDARY_TEXT({ boundaryName: action.boundary.label })
        };
    };

    return {
        handleBoundaryRemove,
        handleBoundaryToggle,
        handleModalClose,
        handleModalConfirm,
        handlePrimaryRemove,
        handlePrimarySelected,
        isModalOpen: pendingAction !== null,
        modalConfig: pendingAction ? getModalConfig(pendingAction) : null,
        primaryBoundary
    };
};

export default useBoundarySelections;
